import type { Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { debounce } from 'lodash-es';
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import { useRoute } from 'vue-router';

import type {
  IAffiliateProgramCreativeFormFields,
  IAffiliateProgramCreativeFormSchema,
  IAffiliateProgramCreativeOptionsResponse,
} from '@modules/affiliate-program-creatives/submodules/edit/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES } from '@modules/affiliate-program-creatives/constants';
import {
  AFFILIATE_PROGRAM_CREATIVES_BULK_ADD_ROUTE_NAME,
  AFFILIATE_PROGRAM_CREATIVES_EDIT_ROUTE_NAME,
} from '@modules/affiliate-program-creatives/submodules/edit/constants';
import {
  useAffiliateProgramCreativeCompaniesQuery,
  useAffiliateProgramCreativeDefaultCompany,
} from '@modules/affiliate-program-creatives/submodules/edit/queries';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { utcDateToLocal } from '@utils/date';
import {
  assert,
  isDate,
  isOptionalString,
} from '@utils/guards';

export interface AffiliateProgramCreativeSchemaComposable {
  isEdit: Ref<boolean>;
  isFileUpload: Ref<boolean>;
  formSchema: Ref<IAffiliateProgramCreativeFormSchema>;
  formGroups: Ref<FormGroup<IAffiliateProgramCreativeFormFields>[]>;
  selectedAffiliates: Ref<number[]>;
  onFormChange<
    T extends IAffiliateProgramCreativeFormFields,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void;
  onFormInput<
    T extends IAffiliateProgramCreativeFormFields,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void;
}

export default function useAffiliateProgramCreativeSchema(
  optionsData: Ref<IAffiliateProgramCreativeOptionsResponse | undefined>,
  creativeItem: Ref<IAffiliateProgramCreativeFormFields | undefined>,
): AffiliateProgramCreativeSchemaComposable {
  const { getTranslation } = useI18n();
  const { t } = useTranslation();
  const route = useRoute();
  const companySearchQuery = ref<string>();

  const { data: companiesList, isFetching: isCompaniesFetching } = useAffiliateProgramCreativeCompaniesQuery(companySearchQuery);
  const { data: defaultCompany } = useAffiliateProgramCreativeDefaultCompany();

  const isEdit = computed(() => route.name === AFFILIATE_PROGRAM_CREATIVES_EDIT_ROUTE_NAME);
  const isBulkAdd = computed(() => route.name === AFFILIATE_PROGRAM_CREATIVES_BULK_ADD_ROUTE_NAME);
  const initFormat = ref<string>();
  const startDate = ref<Date>();
  const endDate = ref<Date>();

  const languagesOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!optionsData.value) {
      return [];
    }

    return optionsData.value.language.map((lang) => ({
      value: lang,
      label: t(`creative.list_locales.${lang}`),
    }));
  });

  const companiesAutocompleteOptions = computed<string[]>(() => (
    companiesList.value?.map((item) => item.advertising_company) ?? []
  ));

  const selectedType = computed(() => creativeItem.value?.format || initFormat.value);

  const linkLabel = computed(() => {
    switch (selectedType.value) {
      case 'bannerflow_gif':
        return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'BANNER_LINK').value;
      case 'iframe':
        return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'IFRAME_LINK').value;
      default:
        return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_LINK').value;
    }
  });

  const selectedAffiliates = computed<number[]>(() => {
    if (!creativeItem.value?.exclusive_affiliates) {
      return [];
    }

    return creativeItem.value.exclusive_affiliates.map((item) => item.affiliate_id);
  });

  const affiliateOptions = computed<IFormSelectInputOption<number, 'id', 'value'>[]>(() => {
    if (!optionsData.value) {
      return [];
    }

    return optionsData.value.affiliates;
  });

  const fileInputAccept = computed<string | undefined>(() => {
    switch (selectedType.value) {
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      default:
        return undefined;
    }
  });

  const fileInputDragDropLabel = computed<string | undefined>(() => {
    if (isBulkAdd.value) {
      return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'BULK_DROP_FILES_HERE').value;
    }

    switch (selectedType.value) {
      case 'jpeg':
        return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'JPEG_DROP_FILES_HERE').value;
      case 'png':
        return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'PNG_DROP_FILES_HERE').value;
      case 'gif':
        return getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'GIF_DROP_FILES_HERE').value;
      default:
        return undefined;
    }
  });

  const isFileUpload = computed<boolean>(() => (
    selectedType.value === 'jpeg' || selectedType.value === 'png' || selectedType.value === 'gif'
  ));

  const isIframeLike = computed<boolean>(() => (
    selectedType.value === 'bannerflow_gif' || selectedType.value === 'iframe'
  ));

  const fileInputMaxFileSize = computed<number>(() => (selectedType.value === 'png' ? 2 * 1024 * 1024 : 1024 * 1024));

  const formatOptions = computed<IFormSelectInputOption<string>[]>(() => {
    const allOptions: IFormSelectInputOption<string>[] = [
      {
        value: 'link',
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_LINK').value,
      },
      {
        value: 'jpeg',
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_JPEG').value,
      },
      {
        value: 'png',
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_PNG').value,
      },
      {
        value: 'gif',
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_GIF').value,
      },
      {
        value: 'bannerflow_gif',
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_BANNERFLOW_GIF').value,
      },
      {
        value: 'iframe',
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_IFRAME').value,
      },
    ];

    return isBulkAdd.value
      ? allOptions.filter((item) => ['jpeg', 'png', 'gif'].includes(item.value))
      : allOptions;
  });

  const creativeStartDate = computed(() => (creativeItem.value?.at_beginning_show ? utcDateToLocal(creativeItem.value.at_beginning_show) : undefined));

  const creativeEndDate = computed(() => (creativeItem.value?.at_end_show ? utcDateToLocal(creativeItem.value.at_end_show) : undefined));

  const startDateValue = computed(() => startDate.value ?? creativeStartDate.value);

  const endDateValue = computed(() => endDate.value ?? creativeEndDate.value);

  const statusOptions = computed<IFormSelectInputOption<boolean>[]>(() => [
    {
      value: true,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'STATUS_ACTIVE').value,
    },
    {
      value: false,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'STATUS_NOT_ACTIVE').value,
    },
  ]);

  const transferAdditionalRequestOptions = computed<IFormSelectInputOption<boolean>[]>(() => [
    {
      value: true,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TRANSFER_ADDITIONAL_REQUEST_PARAMETERS_OPTION_YES').value,
    },
    {
      value: false,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TRANSFER_ADDITIONAL_REQUEST_PARAMETERS_OPTION_NO').value,
    },
  ]);

  const formSchema = computed<IAffiliateProgramCreativeFormSchema>(() => {
    const schema: IAffiliateProgramCreativeFormSchema = {
      format: {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_FORMAT').value,
        type: EFormInputTypes.SELECT,
        value: selectedType.value,
        readonly: isEdit.value,
        required: true,
        options: formatOptions.value,
      },
      advertising_company: {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_COMPANY').value,
        type: EFormInputTypes.TEXT,
        value: creativeItem.value?.advertising_company || defaultCompany.value?.name,
        autocompleteOptions: companiesAutocompleteOptions.value,
        isAutocompleteOptionsLoading: isCompaniesFetching.value,
        autocompleteServerFiltering: true,
        required: true,
      },
      language: {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_LANGUAGE').value,
        type: EFormInputTypes.SELECT,
        options: languagesOptions.value,
        required: true,
        value: creativeItem.value?.language,
      },
      status: {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_STATUS').value,
        type: EFormInputTypes.SELECT,
        options: statusOptions.value,
        value: creativeItem.value?.status,
      },
      at_beginning_show: {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_AT_BEGINNING_SHOW').value,
        type: EFormInputTypes.DATE,
        value: startDateValue.value,
        required: true,
        maxDate: endDateValue.value,
      },
      at_end_show: {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_AT_END_SHOW').value,
        type: EFormInputTypes.DATE,
        value: endDateValue.value,
        required: true,
        minDate: startDateValue.value,
      },
      link: {
        label: linkLabel.value,
        type: EFormInputTypes.TEXTAREA,
        value: creativeItem.value?.link,
        required: true,
      },
      affiliate_ids: {
        type: EFormInputTypes.MULTISELECT_BOX,
        options: affiliateOptions.value,
        optionValue: 'id',
        optionLabel: 'value',
        value: selectedAffiliates.value,
      },
      transfer_additional_request_parameters: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TRANSFER_ADDITIONAL_REQUEST_PARAMETERS_LABEL').value,
        options: transferAdditionalRequestOptions.value,
        value: creativeItem.value?.transfer_additional_request_parameters ?? false,
      },
    };

    if (isIframeLike.value) {
      schema.body = {
        type: EFormInputTypes.TEXT,
        label: getTranslation(
          TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
          selectedType.value === 'bannerflow_gif' ? 'BANNER_IFRAME_BODY' : 'IFRAME_IFRAME_BODY',
        ).value,
        value: creativeItem.value?.body,
      };
      schema.width = {
        type: EFormInputTypes.NUMBER,
        label: getTranslation(
          TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
          selectedType.value === 'bannerflow_gif' ? 'BANNER_IFRAME_WIDTH' : 'IFRAME_IFRAME_WIDTH',
        ).value,
        value: creativeItem.value?.width,
        maxFractionDigits: 0,
      };
      schema.height = {
        type: EFormInputTypes.NUMBER,
        label: getTranslation(
          TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
          selectedType.value === 'bannerflow_gif' ? 'BANNER_IFRAME_HEIGHT' : 'IFRAME_IFRAME_HEIGHT',
        ).value,
        value: creativeItem.value?.height,
        maxFractionDigits: 0,
      };
    }

    if (!isBulkAdd.value) {
      schema.title = {
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'FORM_TITLE').value,
        type: EFormInputTypes.TEXT,
        value: creativeItem.value?.title,
        required: true,
      };
    }

    if (isFileUpload.value) {
      schema.imageUpload = {
        type: EFormInputTypes.FILES,
        label: getTranslation(
          TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
          isBulkAdd.value ? 'FORM_BULK_UPLOAD_LABEL' : 'FORM_CHOOSE_FILE',
        ).value,
        required: !isEdit.value,
        accept: fileInputAccept.value,
        format: selectedType.value,
        dragAndDropLabel: fileInputDragDropLabel.value,
        maxSizeBytes: fileInputMaxFileSize.value,
        fileLimit: isBulkAdd.value ? undefined : 1,
        iconType: 'image',
        renameFiles: isBulkAdd.value,
      };

      schema.fileUploadDescription = {
        type: EFormInputTypes.HIDDEN,
        label: getTranslation(
          TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
          isBulkAdd.value ? 'BULK_UPLOAD_DESCRIPTION' : 'UPLOAD_FILE_DESCRIPTION',
        ).value,
      };
    }

    return schema;
  });

  const formGroups = computed<FormGroup<IAffiliateProgramCreativeFormFields>[]>(() => ([
    {
      items: isBulkAdd.value
        ? [
            'format',
            'advertising_company',
            'language',
            'status',
            'at_beginning_show',
            'at_end_show',
            'transfer_additional_request_parameters',
            'link',
            'affiliate_ids',
            'imageUpload',
            'fileUploadDescription',
          ]
        : [
            'format',
            'title',
            'advertising_company',
            'language',
            'status',
            'at_beginning_show',
            'at_end_show',
            'transfer_additional_request_parameters',
            'link',
            'body',
            'width',
            'height',
            'imageUpload',
            'affiliate_ids',
            'fileUploadDescription',
          ],
    },
  ]));

  function onFormChange<
    T extends IAffiliateProgramCreativeFormFields,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void {
    if (key === 'format') {
      assert(isOptionalString(value));
      initFormat.value = value;
    }

    if (key === 'at_beginning_show') {
      startDate.value = isDate(value) ? value : undefined;
    }

    if (key === 'at_end_show') {
      endDate.value = isDate(value) ? value : undefined;
    }
  }

  function updateCompanySearchQuery(value?: string): void {
    companySearchQuery.value = value;
  }

  const updateCompanySearchQueryDebounced = debounce(updateCompanySearchQuery, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

  function onFormInput<
    T extends IAffiliateProgramCreativeFormFields,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void {
    if (key === 'advertising_company') {
      assert(isOptionalString(value));
      updateCompanySearchQueryDebounced(value);
    }
  }

  onMounted(() => {
    if (!isEdit.value) {
      initFormat.value = window.history.state?.format || 'link';

      if (isBulkAdd.value && !isFileUpload.value) {
        initFormat.value = 'jpeg';
      }
    }
  });

  return {
    isEdit,
    isFileUpload,
    formSchema,
    formGroups,
    selectedAffiliates,
    onFormChange,
    onFormInput,
  };
}
